import type { FundDetail } from "@/data/fund-detail-types";

export function Fund2xRiskBanner({ fund }: { fund: FundDetail }) {
  const isSingleStock = fund.leveragedSubtype === "single-stock";

  // Basket/index path — keep the existing generic Dane banner.
  const isEtfUnderlying = !!fund.underlyingEtfName;
  const underlyingSecurityType = isEtfUnderlying ? "ETF" : "common stock";
  const underlyingDisplay = fund.underlyingEtfName ?? `underlying ${underlyingSecurityType}`;

  const bannerLabel = isSingleStock
    ? "Single-Stock Leveraged ETF - Important Information:"
    : "Leveraged ETF - Important Information:";

  return (
    <div
      className="flex items-start gap-3.5 px-10 py-4 max-md:px-5 max-md:py-3 bg-[rgba(255,170,0,0.06)] border-b border-b-[rgba(255,170,0,0.15)] [[data-theme='light']_&]:bg-[rgba(230,160,0,0.06)] [[data-theme='light']_&]:border-b-[rgba(230,160,0,0.18)]"
      role="note"
    >
      <svg
        className="shrink-0 w-[18px] h-[18px] mt-px text-[#e6a000] [[data-theme='light']_&]:text-[#b87e00] [&_svg]:w-full [&_svg]:h-full"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <p className="font-[family-name:var(--font-instrument)] text-xs leading-[1.7] text-[var(--etf-white2)] font-light tracking-[0.02em] [[data-theme='light']_&]:text-[var(--etf-white)] [&_strong]:font-semibold [&_strong]:text-[#e6a000] [&_strong]:tracking-[0.04em] [[data-theme='light']_&_strong]:text-[#b87e00]">
        {isSingleStock ? (
          <>
            <strong>{bannerLabel}</strong> This is a leveraged (2x) ETF that seeks daily investment
            results corresponding to two times the daily performance of the publicly-traded common
            stock of {fund.underlyingIssuer} ({fund.underlyingListing}: {fund.underlyingTicker})
            (the "Underlying Security"). Due to the effects of compounding, returns over periods
            longer than one day will likely differ from 2x the return of the Underlying Security.
            The Fund concentrates in a single issuer and is designed for short-term trading; it may
            not be suitable for all investors. Please read the prospectus carefully before
            investing.
          </>
        ) : (
          <>
            <strong>{bannerLabel}</strong> This is a leveraged (2x) ETF that seeks daily investment
            results corresponding to two times the daily performance of the {underlyingDisplay}. Due
            to the effects of compounding, returns over periods longer than one day will likely
            differ from 2x the return of the underlying {underlyingSecurityType}. This fund is
            designed for short-term trading and may not be suitable for all investors. Please read
            the prospectus carefully before investing.
          </>
        )}
      </p>
    </div>
  );
}
